import { addDays, differenceInMinutes, isValid, set } from "date-fns";
import React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import z from "zod";

import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons";

import { surveyTimes } from "@performyard/engagement";
import { EngagementSettingsSchema } from "@performyard/models";
import {
	FormDatePicker,
	FormHandle,
	FormSingleSelect,
	FormSubscribe,
	Icon,
	InputWrapper,
	PYForm,
	ResetButton,
	SubmitButton,
	Text,
	Toggle,
	VALIDATE_ON,
	toast,
} from "@performyard/py-components";

import EngagementSettingsSurveySchedule from "../components/EngagementSettingsSurveySchedule";
import { SettingLabel } from "../components/SettingLabel";
import SettingsSection from "../components/SettingsSection";

import { useCompanyEngagementSettings, useCompanySettings } from "../../../../../../api/hooks/defs/company";
import { useSetCompanyEngagementSettings, useSetEngagementEnabled } from "../../../../../../api/hooks/defs/engagement";
import useUnsavedChanges from "../../../../../../hooks/useUnsavedChanges";

// PY-11089 - disable in-email answering for all companies (for now)
const DISABLE_IN_EMAIL_ANSWERING = true;

const EngagementSettingsSchemaSlice = EngagementSettingsSchema.pick({
	employee_status_for_surveys: true,
	frequency: true,
	next_survey_date: true,
	survey_time: true,
	send_reminder_emails: true,
	in_email_answering: true,
});

const employeeStatusForSurveysOptions: { value: string; label: string }[] = [
	{
		value: "active",
		label: "Active",
	},
	{
		value: "invited",
		label: "Invited",
	},
];

const pulseFrequencyOptions: { value: number; label: string }[] = [
	{
		value: 14,
		label: "Every 2 Weeks",
	},
	{
		value: 21,
		label: "Every 3 Weeks",
	},
	{
		value: 28,
		label: "Every 4 Weeks",
	},
	{
		value: 91,
		label: "Every 3 Months",
	},
	{
		value: 182,
		label: "Every 6 Months",
	},
	{
		value: 364,
		label: "Every 12 Months",
	},
];

const surveyTimeOptions: { value: string; label: string }[] = [
	{
		value: "morning",
		label: "Morning",
	},
	{
		value: "mid-day",
		label: "Mid-Day",
	},
	{
		value: "afternoon",
		label: "Afternoon",
	},
	{
		value: "evening",
		label: "Evening",
	},
];

const sendReminderEmailsOptions: { value: boolean; label: string }[] = [
	{
		value: true,
		label: "Yes",
	},
	{
		value: false,
		label: "No",
	},
];

const UnsavedChangesTracker = ({
	isDirty,
	setIsUnsafe,
	setIsSafe,
}: {
	isDirty: boolean;
	setIsUnsafe: () => void;
	setIsSafe: () => void;
}) => {
	useEffect(() => {
		if (isDirty) {
			setIsUnsafe();
		} else {
			setIsSafe();
		}
	}, [isDirty, setIsUnsafe, setIsSafe]);
	return null;
};

const SettingsTab = () => {
	const formRef = useRef<FormHandle<typeof EngagementSettingsSchemaSlice>>(null);
	const [dateWarning, setDateWarning] = useState<string>("");

	// queries
	const { data: engagementSettings, isLoading } = useCompanyEngagementSettings();
	const { data: companySettings } = useCompanySettings();

	const { mutate: setCompanyEngagementSettings } = useSetCompanyEngagementSettings({
		onSuccess: () => {
			toast.success("Your changes have been saved.");
			setIsSafe();
			formRef.current?.reset();
		},
	});
	const { mutate: setEngagementEnabled, isPending: isSettingEngagementEnabled } = useSetEngagementEnabled({
		onSuccess: (_, variables) => {
			toast.success(`Pulse Surveys ${variables?.enabled ? "Enabled" : "Disabled"}`);
		},
	});

	const isSettingsSwapPending = isSettingEngagementEnabled;

	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	// Timezone and date validation helpers
	const timezoneSetting = useMemo(() => {
		return companySettings?.default_timezone || "America/New_York";
	}, [companySettings]);

	const surveyTimeAdjustedDate = (date: Date, surveyTime: string) => {
		return new Date(set(new Date(date), surveyTimes[surveyTime as keyof typeof surveyTimes]));
	};

	const dateInFuture = (nextDate: Date | null) => {
		if (!nextDate) {
			return false;
		}

		// Convert dates to company timezone for comparison
		const currentDate = new Date(new Date().toLocaleString("en-US", { timeZone: timezoneSetting }));
		const convertedNextDate = new Date(nextDate.toLocaleString("en-US", { timeZone: timezoneSetting }));

		return differenceInMinutes(convertedNextDate, currentDate) > 0;
	};

	/**
	 * Adjusts the date if it's in the past, and optionally shows a warning message.
	 * This ensures users can't accidentally schedule surveys in the past.
	 */
	const adjustDateIfPast = (date: Date | string | null, time: string, notify?: boolean): Date | null => {
		if (!date) {
			return addDays(surveyTimeAdjustedDate(new Date(), time), 1);
		}

		const dateObj = typeof date === "string" ? new Date(date) : date;

		if (!isValid(dateObj)) {
			if (notify) {
				setDateWarning("Please select a valid date.");
			}
			return null;
		}

		const adjustedDate = surveyTimeAdjustedDate(dateObj, time);

		if (dateInFuture(adjustedDate)) {
			if (notify) {
				setDateWarning("");
			}
			return adjustedDate;
		} else {
			if (notify) {
				setDateWarning(
					"Please note that the next pulse date was automatically changed to tomorrow as your selected date and preferred pulse time have already passed."
				);
			}
			return addDays(surveyTimeAdjustedDate(new Date(), time), 1);
		}
	};

	// Reset form after data loads to ensure it's not dirty on initial load
	useEffect(() => {
		if (engagementSettings && formRef.current) {
			formRef.current.reset();
		}
	}, [isLoading]);

	const handleSubmit = (values: z.infer<typeof EngagementSettingsSchemaSlice>) => {
		// Validate and adjust date if needed before submission
		const dateValue = new Date(values.next_survey_date);
		const adjustedDate = adjustDateIfPast(dateValue, values.survey_time, false);

		setCompanyEngagementSettings({
			...values,
			next_survey_date: adjustedDate || values.next_survey_date,
		});
	};

	if (isLoading) return <div>Loading...</div>;

	if (!engagementSettings) throw new Error("No engagement settings found");

	return (
		<PYForm
			ref={formRef}
			validateOn={[VALIDATE_ON.ON_SUBMIT]}
			schema={EngagementSettingsSchemaSlice}
			defaultValues={EngagementSettingsSchemaSlice.parse(engagementSettings?.engagement)}
			onSubmit={({ value }) => {
				handleSubmit(value);
			}}
			id="engagement-settings-form"
		>
			<div className="w-full pt-10" key="form-content">
				<SettingsSection
					title="Pulse Surveys"
					description="Pulse surveys are short surveys sent through email to your employees to collect employee engagement and satisfaction data."
				>
					<div className="space-y-8">
						<InputWrapper
							labelPosition="left"
							justify="between"
							id="engagement-enabled"
							label={
								<SettingLabel
									title="Enable Pulse Surveys"
									description="Disabling this setting stops or pauses the sending of pulse surveys."
								/>
							}
						>
							<Toggle
								checked={engagementSettings?.engagement.enabled}
								onCheckedChange={() =>
									setEngagementEnabled({
										enabled: !engagementSettings?.engagement.enabled,
										next_survey_date:
											engagementSettings?.engagement.next_survey_date ||
											addDays(
												surveyTimeAdjustedDate(
													new Date(),
													engagementSettings?.engagement.survey_time || "mid-day"
												),
												1
											),
									})
								}
								color={engagementSettings?.engagement.enabled ? "primary" : "neutral"}
								size={24}
								showText
							/>
						</InputWrapper>
						{DISABLE_IN_EMAIL_ANSWERING ? null : (
							<InputWrapper
								labelPosition="left"
								justify="between"
								id="in-email-answering"
								label={
									<SettingLabel
										title="Enable In-Email Answering"
										description="Disabling this setting stops the first question from displaying in email."
									/>
								}
							>
								<Toggle
									checked={engagementSettings?.engagement.in_email_answering === false ? false : true}
									onCheckedChange={() =>
										setCompanyEngagementSettings({
											in_email_answering:
												engagementSettings?.engagement.in_email_answering === false
													? true
													: false,
										})
									}
									color={
										engagementSettings?.engagement.in_email_answering === false
											? "neutral"
											: "primary"
									}
									size={20}
									showText
								/>
							</InputWrapper>
						)}
						<FormSingleSelect
							id="employee_status_for_surveys"
							labelPosition="left"
							justify="between"
							label={
								<SettingLabel
									title="Employee Status for Pulse Surveys"
									description="Set the status employees must be in to receive surveys"
									tooltip="PerformYard will use this setting to determine if surveys should be sent to all Active employees, or just those that have been invited. This setting will only apply to surveys sent after the setting is saved and is not retroactive."
								/>
							}
							options={employeeStatusForSurveysOptions}
							dangerouslySetClassName="max-w-[197px] ml-auto"
						/>
					</div>
				</SettingsSection>
				<div className="h-8 w-full border-t border-solid border-neutral-2" />
				<SettingsSection
					title="Pulse Survey Schedule"
					description="Configure the date, frequency, and time for your pulse survey schedule. "
				>
					<div className="space-y-8">
						<div className="space-y-2">
							<FormDatePicker
								id="next_survey_date"
								labelPosition="left"
								justify="between"
								label={
									<SettingLabel
										title="Next Pulse Date"
										description="Set the date for the next pulse survey"
										tooltip="Date that the next engagement survey should be sent."
									/>
								}
								dangerouslySetClassName="max-w-[197px] ml-auto"
							/>
							<FormSubscribe
								id="dateValidation"
								selector={(state) => ({
									nextSurveyDate: state.values.next_survey_date,
									surveyTime: state.values.survey_time,
								})}
							>
								{({ nextSurveyDate, surveyTime }) => {
									// Check if date needs adjustment and show warning
									useEffect(() => {
										if (nextSurveyDate && surveyTime) {
											const dateObj = new Date(nextSurveyDate);
											const adjustedDate = surveyTimeAdjustedDate(dateObj, surveyTime);

											if (!isValid(dateObj)) {
												setDateWarning("Please select a valid date.");
											} else if (!dateInFuture(adjustedDate)) {
												setDateWarning(
													"Please note that the next pulse date will be automatically adjusted to tomorrow as your selected date and preferred pulse time have already passed."
												);
											} else {
												setDateWarning("");
											}
										}
									}, [nextSurveyDate, surveyTime]);

									// Display warning if date needs adjustment
									if (dateWarning) {
										return (
											<div className="flex items-start gap-2" data-testid="date-adjusted-warning">
												<Icon icon={faExclamationCircle} size={16} color="red-6" />
												<Text size="caption" weight="regular" color="red-6">
													{dateWarning}
												</Text>
											</div>
										);
									}

									return null;
								}}
							</FormSubscribe>
						</div>
						<FormSingleSelect<number>
							id="frequency"
							labelPosition="left"
							justify="between"
							label={
								<SettingLabel
									title="Pulse Frequency"
									description="Set how often your employees will receive pulse surveys."
									tooltip="Frequency of pulse surveys impacts the number of questions sent per survey. More frequent surveys ask fewer questions, while less frequent surveys will ask additional questions."
								/>
							}
							options={pulseFrequencyOptions}
							dangerouslySetClassName="max-w-[197px] ml-auto"
						/>
						<FormSingleSelect<string>
							id="survey_time"
							labelPosition="left"
							justify="between"
							label={
								<SettingLabel
									title="Preferred Pulse Time of Day"
									description="Set the preferred time of day for sending pulse surveys."
									tooltip="PerformYard will attempt to send pulse surveys at the selected time relative to the company's timezone setting."
								/>
							}
							options={surveyTimeOptions}
							dangerouslySetClassName="max-w-[197px] ml-auto"
						/>
						<FormSingleSelect<boolean>
							id="send_reminder_emails"
							labelPosition="left"
							justify="between"
							label={
								<SettingLabel
									title="Send Reminder Emails"
									description="Set whether to send reminder emails for pulse surveys."
									tooltip="PerformYard will automatically send reminders to complete the survey to employees who have not responded to the original email. The number of reminders will vary based on the selected survey cadence but will not exceed three reminders."
								/>
							}
							options={sendReminderEmailsOptions}
							dangerouslySetClassName="max-w-[197px] ml-auto"
						/>
						<FormSubscribe id="engagementSettingsUnsavedChanges" selector={(state) => [state?.isDirty]}>
							{([isDirty]) => (
								<UnsavedChangesTracker
									isDirty={isDirty}
									setIsUnsafe={setIsUnsafe}
									setIsSafe={setIsSafe}
								/>
							)}
						</FormSubscribe>
						<FormSubscribe
							id="engagementSettingsSurveySchedule"
							selector={(state) => ({
								frequency: state.values.frequency,
								nextSurveyDate: state.values.next_survey_date,
								sendReminderEmails: state.values.send_reminder_emails,
							})}
						>
							{(state) => {
								return (
									<EngagementSettingsSurveySchedule
										frequency={state.frequency}
										nextSurveyDate={state.nextSurveyDate}
										questionCycle={engagementSettings?.engagement.question_cycle}
										factorsFrequency={engagementSettings?.engagement.factors_frequency}
										enpsFrequency={engagementSettings?.engagement.enps_frequency}
										customQuestionsFrequency={
											engagementSettings?.engagement.custom_questions_frequency
										}
										customQuestionsIncludeAll={
											engagementSettings?.engagement.custom_questions_include_all
										}
										additionalFeedbackQuestionFrequency={
											engagementSettings?.engagement.additional_feedback_question_frequency
										}
										sendReminderEmails={state.sendReminderEmails}
										manuallySelectedQuestions={
											engagementSettings?.engagement.manually_selected_questions ?? []
										}
										selectionMethod={engagementSettings?.engagement.selection_method ?? "automatic"}
									/>
								);
							}}
						</FormSubscribe>
					</div>
				</SettingsSection>
			</div>
			<FormSubscribe id="engagementSettingsUnsavedChangesFooter" selector={(state) => [state?.isDirty]}>
				{([isDirty]) => {
					return (
						<div
							data-form-dirty={isDirty}
							className="group flex w-full justify-end gap-2"
							key="form-footer"
						>
							<div className="w-fit group-data-[form-dirty=false]:hidden">
								<ResetButton color="neutral" variant="outline">
									Discard
								</ResetButton>
							</div>
							<div className="w-fit">
								<SubmitButton
									color="green"
									loading={isSettingsSwapPending}
									disabled={isSettingsSwapPending || !isDirty}
								>
									Save Changes
								</SubmitButton>
							</div>
						</div>
					);
				}}
			</FormSubscribe>
		</PYForm>
	);
};

export default SettingsTab;

/*
 
*/
